import { debounce, isEqual, sortBy } from "lodash";
import { autorun, makeAutoObservable, runInAction } from "mobx";
import {
  CleanupFunction,
  DbotEvent,
  DecisionContext,
  DbotEventDispatcher,
  DecisionItem,
  DetailsPanel,
  SharesForShareholder,
  shareholdersWithShares,
  toCompanyDetailsPanel,
  WithInitialization,
} from "@/dbot/common/commonDomain";

import { TMessage } from "@/i18n/tt";
import { LocalizedText } from "@/common/common";
import { DecisionType } from "@/common/decisions";
import { DocumentType } from "@/common/documents";
import { atMidnight, isBefore, plusMonths } from "@/utils/Dates";
import { LocalizedTextEditorModel } from "@/text/localizedTextEditorDomain";
import { LocalDateFieldModel, RadioFieldModel, StringFieldModel } from "@/common/commonForms";
import { ShareTypeCode, shareTypeLabel } from "@/common/share";
import { SuggestedCompany } from "@/types/CompanyInfo";
import { ShareholderDTO } from "@/types/Shareholder";
import { NamedOrganisationNumber } from "@/types/Transactions";
import { adminProject } from "@/utils/navigation";
import { navigate, NavigateEvent } from "@/events/eventsDomain";
import { newUuid } from "@/utils/uuid";

export enum MergerDecisionType {
  mergerBetweenWhollyOwnedSubsidiaries = "mergerBetweenWhollyOwnedSubsidiaries",
  mergerBetweenParentAndWhollyOwnedSubsidiary = "mergerBetweenParentAndWhollyOwnedSubsidiary",
}

const mergerTypeLabels: { [key in MergerDecisionType]: TMessage } = {
  [MergerDecisionType.mergerBetweenWhollyOwnedSubsidiaries]: {
    key: "decisions.merger.merger-type.mergerBetweenWhollyOwnedSubsidiaries",
  },
  [MergerDecisionType.mergerBetweenParentAndWhollyOwnedSubsidiary]: {
    key: "decisions.merger.merger-type.mergerBetweenParentAndWhollyOwnedSubsidiary",
  },
};

export enum AnnualAccountsLocation {
  BUSINESS_OFFICE = "BUSINESS_OFFICE",
  WEBSITE = "WEBSITE",
}

const annualAccountsLocationLabels: { [key in AnnualAccountsLocation]: TMessage } = {
  [AnnualAccountsLocation.BUSINESS_OFFICE]: {
    key: "decisions.merger.annual-accounts-location.BUSINESS_OFFICE",
  },
  [AnnualAccountsLocation.WEBSITE]: {
    key: "decisions.merger.annual-accounts-location.WEBSITE",
  },
};

export enum MergerInvoiceRecipient {
  ACQUIRING_COMPANY = "ACQUIRING_COMPANY",
  TRANSFERRING_COMPANY = "TRANSFERRING_COMPANY",
  EACH_COMPANY = "EACH_COMPANY",
  OTHER_COMPANY = "OTHER_COMPANY",
}

export enum SubsidiaryMergerCompanyRole {
  ACQUIRING = "ACQUIRING",
  TRANSFERRING = "TRANSFERRING",
}

const companyRoleLabels: { [key in SubsidiaryMergerCompanyRole]: TMessage } = {
  [SubsidiaryMergerCompanyRole.ACQUIRING]: {
    key: "decisions.merger.role.ACQUIRING",
  },
  [SubsidiaryMergerCompanyRole.TRANSFERRING]: {
    key: "decisions.merger.role.TRANSFERRING",
  },
};

export type SubsidiaryMergerCapitalChange = {
  transferringCompanyShareTypeCode: ShareTypeCode;
  acquiringCompanyShareTypeCode: ShareTypeCode;
};

export type Merger = {
  type: DecisionType.merger;
  mergerDate?: string;
  mergerType?: MergerDecisionType;
  companyRole?: SubsidiaryMergerCompanyRole;
  acquiringCompany?: NamedOrganisationNumber;
  transferringCompany?: NamedOrganisationNumber;
  acquiringCompanyAddress?: string;
  acquiringCompanyCity?: string;
  acquiringCompanyParent?: NamedOrganisationNumber;
  transferringCompanyAddress?: string;
  transferringCompanyCity?: string;
  transferringCompanyParent?: NamedOrganisationNumber;
  accountingEffectiveDate?: string;
  acquiringCompanyTakesOverManagement?: boolean;
  transferringCompanyHasIssuedFinancialInstruments?: boolean;
  financialInstrumentsDescription?: LocalizedText[];
  boardMembersOrCeoHaveSpecialRights?: boolean;
  specialRightsDescription?: LocalizedText[];
  shareholderNoticeDeadlineWaived?: boolean;
  annualAccountsLocation?: AnnualAccountsLocation;
  capitalChanges: SubsidiaryMergerCapitalChange[];
  invoiceRecipient?: MergerInvoiceRecipient;
  invoiceRecipientCompany?: NamedOrganisationNumber;
  counterpartProjectId?: string;
  counterpartDecisionId?: string;
  hasMergerPlanDocument?: boolean;
};

export const merger = (data?: Partial<Merger>): Merger => ({
  type: DecisionType.merger,
  mergerDate: data?.mergerDate,
  mergerType: data?.mergerType,
  companyRole: data?.companyRole,
  acquiringCompany: data?.acquiringCompany,
  transferringCompany: data?.transferringCompany,
  acquiringCompanyAddress: data?.acquiringCompanyAddress,
  acquiringCompanyCity: data?.acquiringCompanyCity,
  acquiringCompanyParent: data?.acquiringCompanyParent,
  transferringCompanyAddress: data?.transferringCompanyAddress,
  transferringCompanyCity: data?.transferringCompanyCity,
  transferringCompanyParent: data?.transferringCompanyParent,
  accountingEffectiveDate: data?.accountingEffectiveDate,
  acquiringCompanyTakesOverManagement: data?.acquiringCompanyTakesOverManagement,
  transferringCompanyHasIssuedFinancialInstruments:
    data?.transferringCompanyHasIssuedFinancialInstruments,
  financialInstrumentsDescription: data?.financialInstrumentsDescription,
  boardMembersOrCeoHaveSpecialRights: data?.boardMembersOrCeoHaveSpecialRights,
  specialRightsDescription: data?.specialRightsDescription,
  shareholderNoticeDeadlineWaived: data?.shareholderNoticeDeadlineWaived,
  annualAccountsLocation: data?.annualAccountsLocation,
  capitalChanges: data?.capitalChanges ?? [],
  invoiceRecipient: data?.invoiceRecipient,
  invoiceRecipientCompany: data?.invoiceRecipientCompany,
  counterpartProjectId: data?.counterpartProjectId,
  counterpartDecisionId: data?.counterpartDecisionId,
  hasMergerPlanDocument: data?.hasMergerPlanDocument,
});

export const ownCompanyOf = (data: Merger): NamedOrganisationNumber | undefined => {
  switch (data.companyRole) {
    case SubsidiaryMergerCompanyRole.ACQUIRING:
      return data.acquiringCompany;
    case SubsidiaryMergerCompanyRole.TRANSFERRING:
      return data.transferringCompany;
    default:
      return undefined;
  }
};

export const counterpartyOf = (data: Merger): NamedOrganisationNumber | undefined => {
  switch (data.companyRole) {
    case SubsidiaryMergerCompanyRole.ACQUIRING:
      return data.transferringCompany;
    case SubsidiaryMergerCompanyRole.TRANSFERRING:
      return data.acquiringCompany;
    default:
      return undefined;
  }
};

const mergerPartiesOf = (data: Merger) => {
  const { acquiringCompany, transferringCompany } = data;
  if (!acquiringCompany || !transferringCompany) {
    return undefined;
  }
  const acquiring = { company: acquiringCompany, role: SubsidiaryMergerCompanyRole.ACQUIRING };
  const transferring = {
    company: transferringCompany,
    role: SubsidiaryMergerCompanyRole.TRANSFERRING,
  };
  switch (data.companyRole) {
    case SubsidiaryMergerCompanyRole.ACQUIRING:
      return { own: acquiring, counterparty: transferring };
    case SubsidiaryMergerCompanyRole.TRANSFERRING:
      return { own: transferring, counterparty: acquiring };
    default:
      return undefined;
  }
};

export const findMergerWithCounterpart = (decisions: DecisionItem[]): Merger | undefined =>
  decisions
    .map((d) => d.decisionData)
    .find(
      (d): d is Merger => d?.type === DecisionType.merger && d.counterpartProjectId !== undefined,
    );

export const getMergerCompanyPanels = (data: Merger): DetailsPanel[] => {
  const mergerParties = mergerPartiesOf(data);
  const counterpartProjectId = data.counterpartProjectId;
  if (!mergerParties || !counterpartProjectId) {
    return [];
  }
  const { own, counterparty } = mergerParties;
  return [
    {
      ...toCompanyDetailsPanel({ ...own.company, isMainCompany: true }),
      label: companyRoleLabels[own.role],
    },
    {
      ...toCompanyDetailsPanel(counterparty.company),
      label: companyRoleLabels[counterparty.role],
      customOpenEvent: openCounterpartProjectEvent(
        counterparty.company.orgNo,
        counterpartProjectId,
      ),
    },
  ];
};

export const openCounterpartProjectEvent = (orgNo: string, projectId: string): NavigateEvent =>
  navigate(adminProject({ orgNo, projectId }));

export const ownParentOf = (data: Merger): NamedOrganisationNumber | undefined => {
  switch (data.companyRole) {
    case SubsidiaryMergerCompanyRole.ACQUIRING:
      return data.acquiringCompanyParent;
    case SubsidiaryMergerCompanyRole.TRANSFERRING:
      return data.transferringCompanyParent;
    default:
      return undefined;
  }
};

export const counterpartyParentOf = (data: Merger): NamedOrganisationNumber | undefined => {
  switch (data.companyRole) {
    case SubsidiaryMergerCompanyRole.ACQUIRING:
      return data.transferringCompanyParent;
    case SubsidiaryMergerCompanyRole.TRANSFERRING:
      return data.acquiringCompanyParent;
    default:
      return undefined;
  }
};

export const soleCorporateParentOf = (
  shareholders: ShareholderDTO[],
  sharesForShareholders: SharesForShareholder[],
): NamedOrganisationNumber | undefined => {
  const owners = shareholdersWithShares(shareholders, sharesForShareholders).filter(
    (s) => s.companyNumber !== s.organizationNumber,
  );
  const parent = owners.length === 1 ? owners[0] : undefined;
  if (!parent?.isCompany || !parent.companyNumber) {
    return undefined;
  }
  return { name: parent.companyName ?? "", orgNo: parent.companyNumber };
};

export type ParentCompanyStatus =
  | { kind: "no-own-parent" }
  | { kind: "unconfirmed" }
  | { kind: "different-parents" }
  | { kind: "common-parent"; parent: NamedOrganisationNumber }
  | { kind: "subsidiary-not-wholly-owned" }
  | { kind: "parent-unconfirmed" }
  | { kind: "not-the-parent" }
  | {
      kind: "parent-owns-subsidiary";
      parent: NamedOrganisationNumber;
      subsidiary: NamedOrganisationNumber;
    };

export const getParentCompanyStatus = (form: MergerForm): ParentCompanyStatus | undefined => {
  const counterparty = counterpartyOf(form.data);
  if (counterparty === undefined) {
    return undefined;
  }
  if (form.selectedCompany?.organizationnumber !== counterparty.orgNo) {
    return undefined;
  }
  if (form.data.mergerType === MergerDecisionType.mergerBetweenParentAndWhollyOwnedSubsidiary) {
    return getParentSubsidiaryStatus(form, counterparty);
  }
  const ownParent = ownParentOf(form.data);
  if (ownParent === undefined) {
    return { kind: "no-own-parent" };
  }
  const counterpartyParent = counterpartyParentOf(form.data);
  if (counterpartyParent === undefined) {
    return { kind: "unconfirmed" };
  }
  if (counterpartyParent.orgNo !== ownParent.orgNo) {
    return { kind: "different-parents" };
  }
  return { kind: "common-parent", parent: ownParent };
};

export const getShareholderNoticeDeadlineWarning = (form: MergerForm): Date | undefined => {
  if (!form.isParentSubsidiaryMerger) {
    return undefined;
  }
  if (form.companyRole.value !== SubsidiaryMergerCompanyRole.ACQUIRING) {
    return undefined;
  }
  if (form.noticeDeadlineWaived.value) {
    return undefined;
  }
  const boardMeetingDate = form.context.boardMeetingFields.meetingStartDate.toISOString();
  if (boardMeetingDate === undefined) {
    return undefined;
  }
  const noticeDocument = form.context.jsonData.documents.find(
    (d) =>
      d.documentData.type === DocumentType.custom_document &&
      d.documentData.customDocumentType === "mergerShareholderNotice",
  );
  const noticeSentAt =
    noticeDocument &&
    form.context.documentStatuses.find((s) => s.documentId === noticeDocument.documentId)?.sentAt;
  const earliestBoardMeetingDate = atMidnight(plusMonths(noticeSentAt ?? new Date(), 1));
  return isBefore(atMidnight(boardMeetingDate), earliestBoardMeetingDate)
    ? earliestBoardMeetingDate
    : undefined;
};

export const acquiringCompanyName = (form: MergerForm): string | TMessage =>
  form.companyRole.value === SubsidiaryMergerCompanyRole.ACQUIRING
    ? form.ownCompany.name
    : (form.selectedCompany?.name ?? { key: "decisions.merger.the-acquiring-company" });

export const transferringCompanyName = (form: MergerForm): string | TMessage =>
  form.companyRole.value === SubsidiaryMergerCompanyRole.ACQUIRING
    ? (form.selectedCompany?.name ?? { key: "decisions.merger.the-transferring-company" })
    : form.ownCompany.name;

export const managementTakeoverQuestion = (form: MergerForm): TMessage => ({
  key: "decisions.merger.management-takeover.question",
  params: {
    acquiringCompany: acquiringCompanyName(form),
    transferringCompany: transferringCompanyName(form),
  },
});

export const financialInstrumentsQuestion = (form: MergerForm): TMessage => ({
  key: "decisions.merger.financial-instruments.question",
  params: { transferringCompany: transferringCompanyName(form) },
});

export const noticeWaiverQuestion = (form: MergerForm): TMessage => ({
  key: "decisions.merger.notice-waiver.question",
  params: {
    acquiringCompany: acquiringCompanyName(form),
    transferringCompany: transferringCompanyName(form),
  },
});

export const capitalChangesTitle = (form: MergerForm): TMessage => ({
  key: "decisions.merger.capital-changes.title",
  params: { transferringCompany: transferringCompanyName(form) },
});
const getParentSubsidiaryStatus = (
  form: MergerForm,
  counterparty: NamedOrganisationNumber,
): ParentCompanyStatus => {
  if (form.data.companyRole === SubsidiaryMergerCompanyRole.ACQUIRING) {
    const subsidiaryParent = counterpartyParentOf(form.data);
    if (subsidiaryParent === undefined) {
      return { kind: "parent-unconfirmed" };
    }
    if (subsidiaryParent.orgNo !== form.ownCompany.orgNo) {
      return { kind: "not-the-parent" };
    }
    return { kind: "parent-owns-subsidiary", parent: form.ownCompany, subsidiary: counterparty };
  }
  const ownParent = ownParentOf(form.data);
  if (ownParent === undefined) {
    return { kind: "subsidiary-not-wholly-owned" };
  }
  if (ownParent.orgNo !== counterparty.orgNo) {
    return { kind: "not-the-parent" };
  }
  return { kind: "parent-owns-subsidiary", parent: counterparty, subsidiary: form.ownCompany };
};

export class MergerForm implements WithInitialization {
  readonly type = DecisionType.merger;

  dispatch: DbotEventDispatcher;
  context: DecisionContext<Merger>;
  data: Merger;
  readonly ownCompany: NamedOrganisationNumber;
  private readonly ownParentOrgNo?: string;

  mergerType: RadioFieldModel<MergerDecisionType | undefined>;
  companyRole: RadioFieldModel<SubsidiaryMergerCompanyRole | undefined>;
  searchCompanyModel: StringFieldModel;
  searchCompanies: (input: string) => void;
  suggestedCompanies: SuggestedCompany[] = [];
  selectedCompany?: SuggestedCompany;
  companyError?: TMessage;
  mergerDate: LocalDateFieldModel;
  accountingEffectiveDate: LocalDateFieldModel;
  managementTakeover: RadioFieldModel<boolean>;
  hasIssuedFinancialInstruments: RadioFieldModel<boolean>;
  financialInstrumentsDescription: LocalizedTextEditorModel;
  hasSpecialRights: RadioFieldModel<boolean>;
  specialRightsDescription: LocalizedTextEditorModel;
  noticeDeadlineWaived: RadioFieldModel<boolean>;
  annualAccountsLocation: RadioFieldModel<AnnualAccountsLocation>;
  invoiceRecipient: RadioFieldModel<MergerInvoiceRecipient>;
  invoiceCompanySearchModel: StringFieldModel;
  searchInvoiceCompanies: (input: string) => void;
  suggestedInvoiceCompanies: SuggestedCompany[] = [];
  selectedInvoiceCompany?: SuggestedCompany;
  capitalChanges: SubsidiaryMergerCapitalChangeModel[];

  constructor(
    dispatch: DbotEventDispatcher,
    context: DecisionContext<Merger>,
    data: Merger = merger(),
  ) {
    makeAutoObservable(this, { dispatch: false }, { autoBind: true });
    this.dispatch = dispatch;
    this.context = context;
    this.data = data;
    this.ownCompany = {
      name: context.initialState.company.name,
      orgNo: context.orgNo,
    };
    this.ownParentOrgNo = soleCorporateParentOf(
      context.initialState.shareholders,
      context.initialState.sharesForShareholders,
    )?.orgNo;

    this.mergerDate = new LocalDateFieldModel({
      value: data.mergerDate,
      required: true,
    });
    this.mergerType = new RadioFieldModel<MergerDecisionType | undefined>({
      value: data.mergerType,
      options: Object.values(MergerDecisionType),
      idFn: (option) => option ?? "",
      labelFn: (option) => (option ? mergerTypeLabels[option] : { translated: "" }),
      required: true,
    });
    this.companyRole = new RadioFieldModel<SubsidiaryMergerCompanyRole | undefined>({
      value: data.companyRole,
      options: Object.values(SubsidiaryMergerCompanyRole),
      idFn: (option) => option ?? "",
      labelFn: (option) => (option ? companyRoleLabels[option] : { translated: "" }),
      required: true,
    });
    const counterparty = counterpartyOf(data);
    this.selectedCompany = counterparty
      ? { name: counterparty.name, organizationnumber: counterparty.orgNo }
      : undefined;
    this.searchCompanyModel = new StringFieldModel({
      value: this.selectedCompany?.name ?? "",
      required: true,
    });
    this.searchCompanies = debounce((input: string) => this.findCompanies(input), 250);
    this.accountingEffectiveDate = new LocalDateFieldModel({
      value: data.accountingEffectiveDate,
      required: true,
    });
    this.managementTakeover = new RadioFieldModel<boolean>({
      value: data.acquiringCompanyTakesOverManagement ?? true,
      options: [true, false],
      idFn: (option) => String(option),
      labelFn: (option) => ({ key: option ? "yes" : "no" }),
    });
    this.hasIssuedFinancialInstruments = new RadioFieldModel<boolean>({
      value: data.transferringCompanyHasIssuedFinancialInstruments ?? false,
      options: [true, false],
      idFn: (option) => String(option),
      labelFn: (option) => ({ key: option ? "yes" : "no" }),
    });
    this.financialInstrumentsDescription = new LocalizedTextEditorModel({
      texts: data.financialInstrumentsDescription,
      aiClient: context.aiClient,
      isReadOnly: () => this.isReadOnly,
      label: { key: "decisions.merger.financial-instruments.description" },
    });
    this.financialInstrumentsDescription.onLanguageChange(
      context.documentLanguages,
      data.financialInstrumentsDescription,
    );
    this.hasSpecialRights = new RadioFieldModel<boolean>({
      value: data.boardMembersOrCeoHaveSpecialRights ?? false,
      options: [true, false],
      idFn: (option) => String(option),
      labelFn: (option) => ({ key: option ? "yes" : "no" }),
    });
    this.specialRightsDescription = new LocalizedTextEditorModel({
      texts: data.specialRightsDescription,
      aiClient: context.aiClient,
      isReadOnly: () => this.isReadOnly,
      label: { key: "decisions.merger.special-rights.description" },
    });
    this.specialRightsDescription.onLanguageChange(
      context.documentLanguages,
      data.specialRightsDescription,
    );
    this.noticeDeadlineWaived = new RadioFieldModel<boolean>({
      value: data.shareholderNoticeDeadlineWaived ?? false,
      options: [true, false],
      idFn: (option) => String(option),
      labelFn: (option) => ({ key: option ? "yes" : "no" }),
    });
    this.annualAccountsLocation = new RadioFieldModel<AnnualAccountsLocation>({
      value: data.annualAccountsLocation ?? AnnualAccountsLocation.BUSINESS_OFFICE,
      options: Object.values(AnnualAccountsLocation),
      idFn: (option) => option,
      labelFn: (option) => annualAccountsLocationLabels[option],
    });
    const invoiceCompany = data.invoiceRecipientCompany;
    this.selectedInvoiceCompany = invoiceCompany
      ? { name: invoiceCompany.name, organizationnumber: invoiceCompany.orgNo }
      : undefined;
    this.invoiceCompanySearchModel = new StringFieldModel({
      value: invoiceCompany?.name ?? "",
      required: data.invoiceRecipient === MergerInvoiceRecipient.OTHER_COMPANY,
      validator: () => (this.selectedInvoiceCompany ? undefined : { key: "required" }),
    });
    this.invoiceRecipient = new RadioFieldModel<MergerInvoiceRecipient>({
      value: data.invoiceRecipient ?? MergerInvoiceRecipient.ACQUIRING_COMPANY,
      options: Object.values(MergerInvoiceRecipient),
      idFn: (option) => option,
      labelFn: (option) => this.invoiceRecipientLabel(option),
      required: true,
      onChange: (recipient) =>
        this.invoiceCompanySearchModel.setRequired(
          recipient === MergerInvoiceRecipient.OTHER_COMPANY,
        ),
    });
    this.searchInvoiceCompanies = debounce(
      (input: string) =>
        this.dispatch([
          { type: "dbot/search-companies", input, source: this.invoiceCompanySearchModel },
        ]),
      250,
    );
    this.capitalChanges = data.capitalChanges.map(
      (change) => new SubsidiaryMergerCapitalChangeModel(change),
    );
  }

  private invoiceRecipientLabel(recipient: MergerInvoiceRecipient): TMessage {
    switch (recipient) {
      case MergerInvoiceRecipient.ACQUIRING_COMPANY:
        return { translated: this.data.acquiringCompany?.name ?? "" };
      case MergerInvoiceRecipient.TRANSFERRING_COMPANY:
        return { translated: this.data.transferringCompany?.name ?? "" };
      case MergerInvoiceRecipient.EACH_COMPANY:
        return { key: "decisions.merger.invoice-recipient.each-company" };
      case MergerInvoiceRecipient.OTHER_COMPANY:
        return { key: "decisions.merger.invoice-recipient.other-company" };
    }
  }

  init(): CleanupFunction {
    return autorun(() => {
      this.syncDescriptionLanguages(
        this.financialInstrumentsDescription,
        this.data.financialInstrumentsDescription,
      );
      this.syncDescriptionLanguages(
        this.specialRightsDescription,
        this.data.specialRightsDescription,
      );
    });
  }

  private syncDescriptionLanguages(editor: LocalizedTextEditorModel, saved?: LocalizedText[]) {
    const languages = this.context.documentLanguages;
    const current = editor.textModels.map((t) => t.locale);
    if (!isEqual(sortBy(languages), sortBy(current))) {
      runInAction(() => {
        editor.onLanguageChange(languages, saved);
      });
    }
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/search-companies-result":
        if (event.source === this) {
          this.suggestedCompanies = event.result.filter(
            (company) => !this.excludedCounterpartyOrgNos.includes(company.organizationnumber),
          );
        }
        if (event.source === this.invoiceCompanySearchModel) {
          this.suggestedInvoiceCompanies = event.result;
        }
        break;
      case "dbot/update-input-state": {
        const data = this.context.decisionData;
        if (!isEqual(this.data, data)) {
          this.data = data;
          if (this.capitalChanges.length === 0) {
            this.syncCapitalChangesWithTransferringCompany();
          }
        }
        break;
      }
    }
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isParentSubsidiaryMerger(): boolean {
    return this.mergerType.value === MergerDecisionType.mergerBetweenParentAndWhollyOwnedSubsidiary;
  }

  private get excludedCounterpartyOrgNos(): string[] {
    return this.isParentSubsidiaryMerger || this.ownParentOrgNo === undefined
      ? [this.ownCompany.orgNo]
      : [this.ownCompany.orgNo, this.ownParentOrgNo];
  }

  get isManagedInCounterpartProject(): boolean {
    return this.data.hasMergerPlanDocument === false;
  }

  get isReadOnly(): boolean {
    return this.isProjectLocked || this.isManagedInCounterpartProject;
  }

  openCounterpartProject() {
    const counterparty = counterpartyOf(this.data);
    const counterpartProjectId = this.data.counterpartProjectId;
    if (counterparty === undefined || counterpartProjectId === undefined) {
      return;
    }
    this.dispatch([openCounterpartProjectEvent(counterparty.orgNo, counterpartProjectId)]);
  }

  setCompanySearch(input: string) {
    this.searchCompanyModel.setInputValue(input);
    this.searchCompanies(input);
  }

  findCompanies(input: string) {
    this.dispatch([{ type: "dbot/search-companies", input, source: this }]);
  }

  selectCompany(company: SuggestedCompany) {
    const changed = company.organizationnumber !== this.selectedCompany?.organizationnumber;
    this.searchCompanyModel.setAndCommitInputValue(company.name);
    this.selectedCompany = company;
    this.companyError = undefined;
    if (changed) {
      this.syncCapitalChangesWithTransferringCompany();
    }
  }

  clearSelectedCompany() {
    const changed = this.selectedCompany !== undefined;
    this.searchCompanyModel.clear();
    this.selectedCompany = undefined;
    this.companyError = undefined;
    if (changed) {
      this.syncCapitalChangesWithTransferringCompany();
    }
  }

  setCompanyRole(role: SubsidiaryMergerCompanyRole | undefined) {
    const changed = role !== this.companyRole.value;
    this.companyRole.setValue(role);
    if (changed) {
      this.syncCapitalChangesWithTransferringCompany();
    }
  }

  private syncCapitalChangesWithTransferringCompany() {
    const isSavedTransferringCompany =
      this.companyRole.value === this.data.companyRole &&
      this.selectedCompany?.organizationnumber === counterpartyOf(this.data)?.orgNo;
    this.capitalChanges = isSavedTransferringCompany
      ? this.data.capitalChanges.map((change) => new SubsidiaryMergerCapitalChangeModel(change))
      : [];
  }

  addCapitalChange() {
    this.capitalChanges.push(new SubsidiaryMergerCapitalChangeModel());
  }

  removeCapitalChange(id: string) {
    this.capitalChanges = this.capitalChanges.filter((change) => change.id !== id);
  }

  setInvoiceCompanySearch(input: string) {
    this.invoiceCompanySearchModel.setInputValue(input);
    this.searchInvoiceCompanies(input);
  }

  selectInvoiceCompany(company: SuggestedCompany) {
    this.selectedInvoiceCompany = company;
    this.invoiceCompanySearchModel.setAndCommitInputValue(company.name);
  }

  clearInvoiceCompany() {
    this.selectedInvoiceCompany = undefined;
    this.invoiceCompanySearchModel.clear();
  }

  get isOtherInvoiceRecipient(): boolean {
    return this.invoiceRecipient.value === MergerInvoiceRecipient.OTHER_COMPANY;
  }

  get isValid(): boolean {
    return (
      (!this.isOtherInvoiceRecipient || this.selectedInvoiceCompany !== undefined) &&
      this.mergerDate.isValid &&
      !this.mergerDate.isEmpty &&
      this.mergerType.value !== undefined &&
      this.companyRole.value !== undefined &&
      this.selectedCompany !== undefined &&
      this.accountingEffectiveDate.isValid &&
      !this.accountingEffectiveDate.isEmpty &&
      this.capitalChanges.every((change) => change.isValid)
    );
  }

  validate(): boolean {
    this.mergerDate.validate();
    this.mergerType.validate();
    this.companyRole.validate();
    this.accountingEffectiveDate.validate();
    this.companyError = this.selectedCompany !== undefined ? undefined : { key: "required" };
    this.invoiceRecipient.validate();
    this.invoiceCompanySearchModel.validate();
    return this.isValid;
  }
}

export class SubsidiaryMergerCapitalChangeModel {
  readonly id = newUuid();
  transferringCompanyShareTypeCode: RadioFieldModel<ShareTypeCode>;
  acquiringCompanyShareTypeCode: RadioFieldModel<ShareTypeCode>;

  constructor(data?: SubsidiaryMergerCapitalChange) {
    makeAutoObservable(this, {}, { autoBind: true });
    const shareTypeCodes = Object.values(ShareTypeCode).filter((s) => s !== ShareTypeCode.UNKNOWN);
    this.transferringCompanyShareTypeCode = new RadioFieldModel({
      value: data?.transferringCompanyShareTypeCode ?? ShareTypeCode.COMMON,
      options: shareTypeCodes,
      idFn: (code) => code,
      labelFn: (code) => shareTypeLabel(code),
    });
    this.acquiringCompanyShareTypeCode = new RadioFieldModel({
      value: data?.acquiringCompanyShareTypeCode ?? ShareTypeCode.COMMON,
      options: shareTypeCodes,
      idFn: (code) => code,
      labelFn: (code) => shareTypeLabel(code),
    });
  }

  get isValid(): boolean {
    return (
      this.transferringCompanyShareTypeCode.isValid && this.acquiringCompanyShareTypeCode.isValid
    );
  }

  toData(): SubsidiaryMergerCapitalChange {
    return {
      transferringCompanyShareTypeCode: this.transferringCompanyShareTypeCode.value,
      acquiringCompanyShareTypeCode: this.acquiringCompanyShareTypeCode.value,
    };
  }
}
